import React, { useEffect, useState } from "react";
import {
  Timeline,
  type TimelineStep,
  type TimelineStepState,
} from "components/common/Timeline";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Link } from "react-router-dom";
import { DateOnly } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  formatSourceChannelLabel,
  priorityMap,
} from "features/Meta/constants/meta.constants";
import {
  PettyWorkStatus,
  pettyWorkStatusMap,
} from "features/PettyWorks/constants/petty-work.constants";
import { pettyWorkLineQuantity } from "features/PettyWorks/utils/petty-work.utils";
import {
  WorkOrderItemTypeLabelMap,
  type WorkOrderItemTypeValue,
} from "features/WorkOrders/constants/work-order-item.constants";
import { usePettyWorkOverview } from "./PettyWorkOverviewContext";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import { ClientPopover } from "components/common/ClientPopover";
import AssignmentsOverviewCard from "features/WorkAssignments/components/AssignmentsOverviewCard";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  shouldShowPettyWorkAssignmentActions,
  canReassignPettyWork,
} from "features/PettyWorks/utils/pettyWorkActionPolicy";
import { getInvoices } from "features/Invoices/services/InvoiceService";
import {
  InvoiceSourceType,
  InvoiceStatus,
  InvoiceStatusMap,
} from "features/Invoices/constants/invoice.constants";
import type { Invoice } from "features/Invoices/types/invoice.types";
import type { PettyWork } from "features/PettyWorks/types/petty-work.types";
import { formatCurrency } from "utils/currency";
import ClientAdvancesCard from "features/ClientAdvances/components/ClientAdvancesCard";
import { ClientAdvanceSourceType } from "features/ClientAdvances/constants/client-advance.constants";

/**
 * Only IN_PROGRESS is genuine ongoing work — NEW (not started), ON_HOLD (paused),
 * and READY_FOR_BILLING (waiting in someone else's queue) are all resting states,
 * not active work, and should render as a static "done" dot.
 */
function resolvePettyWorkTimelineState(
  status: PettyWorkStatus,
  isLast: boolean,
): TimelineStepState {
  if (status === PettyWorkStatus.CANCELLED) return "skipped";
  if (isLast && status === PettyWorkStatus.IN_PROGRESS) return "active";
  return "done";
}

function buildPettyWorkTimeline(pw: PettyWork): TimelineStep[] {
  const history = pw.statusHistory ?? [];

  if (history.length === 0) {
    const s = pw.status as PettyWorkStatus;
    const label = pettyWorkStatusMap[s]?.label ?? s;
    const state = resolvePettyWorkTimelineState(s, true);
    return [
      { label, timestamp: pw.created_at, state, color: pettyWorkStatusMap[s]?.color },
    ];
  }

  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const s = entry.status as PettyWorkStatus;
    const label = pettyWorkStatusMap[s]?.label ?? entry.status;
    const state = resolvePettyWorkTimelineState(s, isLast);

    return {
      label,
      timestamp: entry.at,
      by: entry.user
        ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
        : entry.by != null ? `#${entry.by}` : undefined,
      actor: entry.message ?? undefined,
      state,
      color: pettyWorkStatusMap[s]?.color,
    };
  });
}

function InvoiceCard({
  sourceId,
  updatedAt,
}: {
  sourceId: number;
  /** Re-fetches whenever the petty work changes — e.g. after regenerate-invoice,
   * which may replace the active invoice or correct it in place. */
  updatedAt?: string;
}) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getInvoices({
      sourceType: InvoiceSourceType.PETTY_WORK,
      sourceId,
      limit: 10,
    })
      .then((res) => {
        if (res.success) setInvoices(res.data.rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sourceId, updatedAt]);

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-3">
          <Spinner size="sm" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="text-uppercase mb-0">Invoices</h6>
          <span className="badge bg-light text-dark">{invoices.length}</span>
        </div>
        {invoices.length === 0 ? (
          <p className="text-muted small mb-0">No invoices raised yet.</p>
        ) : (
          <div className="vstack gap-2">
            {invoices.map((inv) => {
              const statusInfo = InvoiceStatusMap[inv.status];
              return (
                <div
                  key={inv.id}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex flex-column gap-1">
                    <Link
                      to={`/finance/invoices/${inv.id}`}
                      className="fw-medium text-body text-decoration-none link"
                    >
                      {inv.code || `Invoice #${inv.id}`}
                    </Link>
                    {inv.issueDate ? (
                      <span className="text-muted small">
                        Issued: <DateOnly date={inv.issueDate} />
                      </span>
                    ) : null}
                    {inv.status === InvoiceStatus.CANCELLED &&
                    inv.cancelReason ? (
                      <span className="text-muted small">
                        Reason: {inv.cancelReason}
                      </span>
                    ) : null}
                  </div>
                  <div className="d-flex flex-column align-items-end gap-1">
                    <Badge
                      color={statusInfo?.color ?? "secondary"}
                      className="fs-11"
                    >
                      {statusInfo?.label ?? inv.status}
                    </Badge>
                    {inv.status === InvoiceStatus.CANCELLED &&
                    inv.cancelledAt ? (
                      <span className="text-muted small">
                        <DateOnly date={inv.cancelledAt} />
                      </span>
                    ) : null}
                    {inv.outstandingAmount > 0 ? (
                      <span className="text-muted small">
                        Due: {formatCurrency(inv.outstandingAmount)}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LineItemsAccordion({ pettyWork }: { pettyWork: PettyWork | null }) {
  const [open, setOpen] = useState("line-items");
  const toggle = (id: string) => setOpen((prev) => (prev === id ? "" : id));

  return (
    <Accordion open={open} toggle={toggle} className="mb-4">
      <AccordionItem>
        <AccordionHeader targetId="line-items">
          <span className="fw-medium">Line items (BOQ)</span>
        </AccordionHeader>
        <AccordionBody accordionId="line-items">
          {pettyWork?.items?.length ? (
            <div className="table-responsive table-accordion">
              <Table className="table-bordered table-nowrap mb-0 align-middle">
                <thead className="table-light text-muted small">
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Product / description</th>
                    <th>Unit</th>
                    <th className="text-end">Rate</th>
                    <th className="text-end">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {pettyWork.items.map((ln, i) => (
                    <tr key={ln.id ?? i}>
                      <td>{i + 1}</td>
                      <td>
                        {ln.type
                          ? (WorkOrderItemTypeLabelMap[
                              ln.type as WorkOrderItemTypeValue
                            ] ?? ln.type)
                          : "—"}
                      </td>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {ln.description?.trim() || "—"}
                      </td>
                      <td>{ln.unit ?? "—"}</td>
                      <td className="text-end tabular-nums">
                        {formatCurrency(ln.rate ?? 0)}
                      </td>
                      <td className="text-end tabular-nums">
                        {pettyWorkLineQuantity(ln)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-muted mb-0">No line items.</p>
          )}
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );
}

const OverviewTab = () => {
  const { pettyWork, refetch } = usePettyWorkOverview();
  const { can } = useAccess();
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);

  return (
    <React.Fragment>
      <Row className="mb-0">
        <Col lg={8}>
          <Card className="mb-4">
            <CardBody>
              <div className="text-muted">
                <h6 className="mb-3 text-uppercase">Petty work info</h6>
                <div className="table-responsive">
                  <Table className="table-borderless">
                    <tbody>
                      {pettyWork?.code ? (
                        <tr>
                          <th
                            className="ps-0"
                            scope="row"
                            style={{ width: "180px" }}
                          >
                            Code :
                          </th>
                          <td className="text-muted">{pettyWork.code}</td>
                        </tr>
                      ) : null}
                      <tr>
                        <th className="ps-0" scope="row">
                          Title :
                        </th>
                        <td className="text-muted">
                          {pettyWork?.title ?? "—"}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Status :
                        </th>
                        <td>
                          <StatusBadge
                            value={pettyWork?.status}
                            map={pettyWorkStatusMap}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Priority :
                        </th>
                        <td>
                          <StatusBadge
                            value={pettyWork?.priorityId}
                            map={priorityMap}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Source channel :
                        </th>
                        <td className="text-muted">
                          {formatSourceChannelLabel(pettyWork?.sourceChannel)}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Client :
                        </th>
                        <td className="text-muted">
                          {pettyWork?.client ? (
                            <ClientPopover
                              client={pettyWork.client}
                              clientId={pettyWork.clientId ?? pettyWork.client.id}
                            >
                              <Link
                                to={`/clients/${pettyWork.clientId}`}
                                className="link fw-medium"
                              >
                                {(() => {
                                  const name = getClientDisplayName(
                                    pettyWork.client,
                                  );
                                  return name !== "—"
                                    ? name
                                    : pettyWork.client.email || "—";
                                })()}
                              </Link>
                            </ClientPopover>
                          ) : pettyWork?.clientId ? (
                            <Link
                              to={`/clients/${pettyWork.clientId}`}
                              className="link fw-medium"
                            >
                              View client
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Planned start :
                        </th>
                        <td className="text-muted">
                          {pettyWork?.plannedStartDate ? (
                            <DateOnly date={pettyWork.plannedStartDate} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Planned end :
                        </th>
                        <td className="text-muted">
                          {pettyWork?.plannedEndDate ? (
                            <DateOnly date={pettyWork.plannedEndDate} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Agreed amount :
                        </th>
                        <td className="text-muted">
                          {pettyWork?.agreedAmount != null
                            ? formatCurrency(pettyWork.agreedAmount)
                            : "—"}
                        </td>
                      </tr>
                      {pettyWork?.finalAmount != null ? (
                        <tr>
                          <th className="ps-0" scope="row">
                            Final amount :
                          </th>
                          <td className="text-muted">
                            {formatCurrency(pettyWork.finalAmount)}
                          </td>
                        </tr>
                      ) : null}
                      {pettyWork?.deleted_at ? (
                        <tr>
                          <th className="ps-0" scope="row">
                            Record :
                          </th>
                          <td>
                            <span className="badge bg-danger">Deleted</span>
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                </div>

                {pettyWork?.description?.trim() ? (
                  <div className="pt-3 border-top border-top-dashed mt-4">
                    <h6 className="mb-2 text-uppercase">Description</h6>
                    <p className="text-muted mb-0">{pettyWork.description}</p>
                  </div>
                ) : null}
              </div>
            </CardBody>
          </Card>

          <LineItemsAccordion pettyWork={pettyWork ?? null} />

          {pettyWork?.id ? (
            <InvoiceCard
              sourceId={pettyWork.id}
              updatedAt={pettyWork.updated_at}
            />
          ) : null}
        </Col>
        <Col lg={4}>
          {pettyWork ? (
            <Card className="mb-3">
              <CardBody>
                <h6 className="text-uppercase mb-3 text-muted">Timeline</h6>
                <Timeline steps={buildPettyWorkTimeline(pettyWork)} />
              </CardBody>
            </Card>
          ) : null}
          {pettyWork?.id ? (
            <div className="mb-3">
              <AssignmentsOverviewCard
                sourceType={WorkAssignmentSourceType.PETTY_WORK}
                sourceId={pettyWork.id}
                sourceLabel={
                  pettyWork.code
                    ? `Petty work ${pettyWork.code}`
                    : pettyWork.title
                      ? `Petty work: ${pettyWork.title}`
                      : `Petty work #${pettyWork.id}`
                }
                canAssign={canCreateAssignment && shouldShowPettyWorkAssignmentActions(pettyWork.status)}
                canReassign={canUpdateAssignment && canReassignPettyWork(pettyWork.status)}
                onSuccess={() => void refetch()}
              />
            </div>
          ) : null}
          {pettyWork?.id ? (
            <ClientAdvancesCard
              className="mt-3"
              sourceType={ClientAdvanceSourceType.PETTY_WORK}
              sourceId={pettyWork.id}
            />
          ) : null}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default OverviewTab;
